// Echo Engine — public gameplay-facing Sticker API.
//
// This is the SINGLE documented import surface for gameplay code. Import types
// and the service from here only. Do NOT import from ./internal/* or from any
// scanner/camera/backend module directly.
//
// StickerService is a stage-owned engine service, constructed in stage.ts
// alongside the other services. Gameplay obtains the shared instance from the
// stage rather than constructing its own:
//
//   import Stage from '../lib/services/stage/stage';
//   const stickers = Stage.instance().sticker_service;
//   const result = await stickers.scan();
//   if (result.status === 'scanned') {
//     const save = result.session.save;          // free-form JSON
//     const tag = result.session.tag;            // { id, name } project tag
//     await stickers.updateSave((prev) => ({ ...prev, score: 10 }));
//   }
//
// (createStickerService() remains available for isolated instances such as
// tests and for the owner's private-adapter swap.)
//
// Cancellation: pass an AbortSignal via scan({ signal }). Aborting yields
// { status: 'cancelled' }. Expected failures resolve to { status: 'error' }
// with a typed StickerError; the promise does not reject for those.

import type { StickerScannerAdapter } from './sticker_adapter';
import {
  JsonObject,
  ProjectTag,
  ScanOptions,
  ScanResult,
  StickerCode,
  StickerError,
  StickerSession,
  UNASSIGNED_TAG,
} from './sticker_types';
import { deepCloneJson } from './internal/clone';
import { StubScannerAdapter } from './internal/stub_scanner_adapter';

// Re-export the public contract so gameplay has one import surface.
export type {
  JsonObject,
  JsonPrimitive,
  JsonValue,
  ProjectTag,
  ScanOptions,
  ScanResult,
  StickerCode,
  StickerCodeStatus,
  StickerErrorCode,
  StickerSession,
} from './sticker_types';
export { StickerError, UNASSIGNED_TAG } from './sticker_types';
export type { StickerScannerAdapter } from './sticker_adapter';

const MAX_SAVE_BYTES = 256 * 1024;

type SessionListener = (session: StickerSession | null) => void;

/**
 * Normalize an adapter-supplied tag into a complete {@link ProjectTag}. A
 * missing tag, or one with a blank id/name, resolves non-destructively to the
 * Unassigned default so legacy adapter results always expose a usable tag.
 */
function normalizeTag(tag: ProjectTag | undefined): ProjectTag {
  if (!tag || typeof tag.id !== 'string' || tag.id.trim().length === 0) {
    return { ...UNASSIGNED_TAG };
  }
  const name =
    typeof tag.name === 'string' && tag.name.trim().length > 0 ? tag.name : tag.id;
  return { id: tag.id, name };
}

function isPlainJsonObject(value: unknown): value is JsonObject {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    return false;
  }
  try {
    JSON.stringify(value);
  } catch {
    return false;
  }
  return true;
}

/**
 * A scanner scene object (the in-scene camera Video). Kept structurally opaque
 * here on purpose: importing the concrete SceneObject/Video would pull the
 * whole Stage + camera + WebGPU graph into this leaf module (and into
 * CommonJS unit-test builds, where capture_service's `import.meta` cannot
 * compile). The concrete type is known only to the owner that supplies the
 * factory (stage.ts).
 */
export type ScannerObject = unknown;

/**
 * Minimal scene port. StickerService only needs to add its owned scanner
 * object to a scene. Depending on this narrow shape (rather than the concrete
 * Scene/SceneObject classes) keeps ownership one-directional and avoids a
 * scene → sticker_service → scene import cycle.
 */
export interface StickerScene {
  add(object: ScannerObject): void;
}

/**
 * Factory the owner (stage.ts) injects to construct the single scanner Video.
 * Invoked exactly once, inside the StickerService constructor, so the service
 * owns the scanner for its whole lifetime.
 */
export type ScannerFactory = () => ScannerObject;

export interface StickerServiceOptions {
  adapter?: StickerScannerAdapter;
  scannerFactory?: ScannerFactory;
}

export class StickerService {
  private readonly adapter: StickerScannerAdapter;
  private session: StickerSession | null = null;
  private listeners = new Set<SessionListener>();

  // StickerService owns the single in-scene camera scanner (Video). The owner
  // injects a factory (stage.ts passes `() => new Video(...)`); it is invoked
  // here so exactly one scanner exists for the app and its lifetime is tied to
  // the service, not to any individual scene. Absent a factory (stub/test
  // adapters) there is no scanner and add() is a no-op.
  private readonly scanner: ScannerObject | null;
  private readonly addedScenes = new WeakSet<StickerScene>();

  constructor(options: StickerServiceOptions = {}) {
    this.adapter = options.adapter ?? new StubScannerAdapter();
    this.scanner = options.scannerFactory ? options.scannerFactory() : null;
  }

  /**
   * Add the service-owned scanner to a scene. Idempotent per scene: a repeated
   * call for the same scene is a no-op, so the single scanner is never added
   * twice. The same instance is reused across scenes.
   */
  add(scene: StickerScene): void {
    if (this.scanner === null) return;
    if (this.addedScenes.has(scene)) return;
    this.addedScenes.add(scene);
    scene.add(this.scanner);
  }

  /**
   * Bring up scanning and return a usable anonymous session with a save game.
   * Never rejects for expected outcomes — inspect the discriminated result.
   */
  async scan<T extends JsonObject = JsonObject>(
    options: ScanOptions = {}
  ): Promise<ScanResult<T>> {
    if (options.signal?.aborted) {
      return { status: 'cancelled' };
    }
    try {
      const adapterSession = await this.adapter.scan({
        signal: options.signal,
        preferredCode: options.preferredCode,
      });
      this.session = {
        sessionId: adapterSession.sessionId,
        code: adapterSession.code,
        saveGameId: adapterSession.saveGameId,
        status: adapterSession.status,
        tag: normalizeTag(adapterSession.tag),
        linkedCodes: [...adapterSession.linkedCodes],
        save: deepCloneJson(adapterSession.save),
      };
      this.notify();
      // Return an independent clone so callers cannot mutate internal state.
      return { status: 'scanned', session: this.getSession<T>()! };
    } catch (err) {
      if (err instanceof StickerError) {
        if (err.code === 'cancelled') return { status: 'cancelled' };
        return { status: 'error', error: err };
      }
      const message = err instanceof Error ? err.message : String(err);
      return {
        status: 'error',
        error: new StickerError('internal', message),
      };
    }
  }

  /** The current session, or null if not scanning. Save data is cloned. */
  getSession<T extends JsonObject = JsonObject>(): StickerSession<T> | null {
    if (!this.session) return null;
    const snapshot: StickerSession = {
      ...this.session,
      tag: { ...this.session.tag },
      linkedCodes: [...this.session.linkedCodes],
      save: deepCloneJson(this.session.save),
    };
    return snapshot as StickerSession<T>;
  }

  /** Cloned free-form save data for the current session, or null. */
  getSave<T extends JsonObject = JsonObject>(): T | null {
    if (!this.session) return null;
    return deepCloneJson(this.session.save) as T;
  }

  /**
   * Update free-form save data. Accepts either a full replacement object or an
   * updater function receiving the current (cloned) save. Persists via the
   * adapter and returns the cloned, stored save.
   */
  async updateSave<T extends JsonObject = JsonObject>(
    next: T | ((prev: T) => T)
  ): Promise<T> {
    if (!this.session) {
      throw new StickerError('not_scanning', 'No active sticker session');
    }
    const prev = deepCloneJson(this.session.save) as T;
    const candidate = typeof next === 'function' ? (next as (p: T) => T)(prev) : next;

    if (!isPlainJsonObject(candidate)) {
      throw new StickerError('invalid_save', 'Save data must be a JSON object');
    }
    const serialized = JSON.stringify(candidate);
    if (serialized.length > MAX_SAVE_BYTES) {
      throw new StickerError('invalid_save', 'Save data exceeds size limit');
    }

    const stored = deepCloneJson(candidate);
    await this.adapter.writeSave(this.session.sessionId, stored);
    this.session = {
      ...this.session,
      save: deepCloneJson(stored),
    };
    this.notify();
    return deepCloneJson(this.session.save) as T;
  }

  /** Codes linked to the current session's save. */
  getLinkedCodes(): StickerCode[] {
    return this.session ? [...this.session.linkedCodes] : [];
  }

  /** End the current session and release adapter state. */
  async endSession(): Promise<void> {
    if (!this.session) return;
    const id = this.session.sessionId;
    this.session = null;
    this.notify();
    await this.adapter.endSession(id);
  }

  /**
   * Subscribe to session changes (scan / updateSave / endSession). Returns an
   * unsubscribe function. Listener receives the cloned session or null.
   */
  subscribe(listener: SessionListener): () => void {
    this.listeners.add(listener);
    listener(this.getSession());
    return () => {
      this.listeners.delete(listener);
    };
  }

  /**
   * Fixture sticker codes for demos. Empty for production adapters that do not
   * expose fixtures.
   */
  getDemoStickers(): StickerCode[] {
    return this.adapter.demoFixtures?.() ?? [];
  }

  private notify(): void {
    const snapshot = this.getSession();
    for (const listener of this.listeners) listener(snapshot);
  }
}

/** Create a StickerService. Defaults to the in-memory stub adapter. */
export function createStickerService(
  options: StickerServiceOptions = {}
): StickerService {
  return new StickerService(options);
}
